import React from 'react';
import {
  Card, BlockStack, InlineStack, Text, Badge, Button, ButtonGroup, Checkbox, Select, TextField
} from '@shopify/polaris';
import StoryEntryCard from '../create/StoryEntryCard';
import { getPrintingControl } from '../../../utils/orderEditDraft';
import { copyPhotos, getLineKid } from '../../../utils/orderKids';

const money = value => Number(value || 0).toFixed(2);

// A replacement keeps the kid name and photos of the story it replaces; the
// user can still change them in the entry card.
const replacementEntry = story => {
  const { kidName, photos } = getLineKid(story.line);
  return {
    key: `${story.key}-replacement`,
    story: null,
    variant: null,
    languageCode: 'en',
    kidName,
    photos: copyPhotos(photos, `${story.key}-replacement`)
  };
};

const hasRaisedOverride = line => {
  const o = line?.price_override;
  return Boolean(o) && typeof o === 'object' && Number(o.unit_price) > Number(o.catalog_price);
};

const RAISED_OVERRIDE_NOTE = 'This story\'s price was raised with an override, so it cannot be removed or replaced here.';

const Changed = () => <Badge tone="attention">Changed</Badge>;

export default function EditableStoryCard({
  story, index, catalog, capabilities, onChange, canRemove, kids = []
}) {
  const caps = capabilities || {};

  if (story.newEntry && story.removed) return null;

  if (story.newEntry) {
    // A new story is not an existing line, so the last-story guard (canRemove) does not apply.
    const title = story.newEntry.story?.title || 'New story';
    const entry = story.newEntry;
    const kidIndex = kids.findIndex(kid => kid.name === entry.kidName);
    const kidOptions = [
      { value: '', label: 'New kid' },
      ...kids.map((kid, i) => ({ value: String(i), label: kid.name }))
    ];
    // Picking a kid copies their name and photos; picking "New kid" clears them.
    const handlePickKid = value => {
      const kid = value === '' ? null : kids[Number(value)];
      onChange({
        ...story,
        newEntry: {
          ...entry,
          kidName: kid ? kid.name : '',
          photos: kid ? copyPhotos(kid.photos, `${story.key}-kid`) : []
        }
      });
    };
    return (
      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <InlineStack gap="200" blockAlign="center">
              <Text as="h3" variant="headingSm">{title}</Text>
              <Badge tone="success">New</Badge>
            </InlineStack>
            <Button
              variant="plain"
              tone="critical"
              onClick={() => onChange({ ...story, removed: true })}
            >
              Remove story
            </Button>
          </InlineStack>
          {kids.length > 0 && (
            <Select
              label="Kid"
              options={kidOptions}
              value={kidIndex === -1 ? '' : String(kidIndex)}
              onChange={handlePickKid}
              helpText="Choose a kid from this order, or enter a new one below"
            />
          )}
          <StoryEntryCard
            entry={story.newEntry}
            index={index}
            catalog={catalog}
            onChange={next => onChange({ ...story, newEntry: next })}
            showErrors
          />
        </BlockStack>
      </Card>
    );
  }

  const printing = getPrintingControl(story);
  const { addons, addonsOriginal } = story;
  const premiumChanged = story.premium !== story.premiumOriginal;
  const messageChanged = addons.giftNote && addonsOriginal.giftNote
    && addons.giftMessage.trim() !== addonsOriginal.giftMessage.trim();
  const setAddons = patch => onChange({ ...story, addons: { ...addons, ...patch } });
  const editingAddons = caps.canToggleAddons && !story.replacement;
  const dimmed = story.removed ? { opacity: 0.5 } : undefined;
  const raised = hasRaisedOverride(story.line);
  const canReplaceNow = caps.canReplace && !story.addonsAmbiguous && !story.replacement && !raised;
  const canRemoveNow = caps.canRemove && canRemove && !story.addonsAmbiguous && !raised;
  const showRaisedNote = raised && !story.removed && (caps.canReplace || caps.canRemove);

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="start" gap="200">
          <div style={dimmed}>
            <BlockStack gap="050">
              <InlineStack gap="200" blockAlign="center">
                <Text as="h3" variant="headingSm">{story.line?.name}</Text>
                {story.removed && <Badge tone="critical">Removed</Badge>}
                {story.replacement && <Badge tone="attention">Replaced</Badge>}
              </InlineStack>
              <Text as="span" tone="subdued">{money(story.line?.price)}</Text>
            </BlockStack>
          </div>
          {story.removed ? (
            <Button onClick={() => onChange({ ...story, removed: false })}>Undo remove</Button>
          ) : (
            <ButtonGroup>
              {canReplaceNow && (
                <Button onClick={() => onChange({ ...story, replacement: replacementEntry(story) })}>
                  Replace story
                </Button>
              )}
              {canRemoveNow && (
                <Button tone="critical" onClick={() => onChange({ ...story, removed: true })}>
                  Remove story
                </Button>
              )}
            </ButtonGroup>
          )}
        </InlineStack>

        {showRaisedNote && (
          <Text as="p" tone="subdued">{RAISED_OVERRIDE_NOTE}</Text>
        )}

        {!story.removed && story.replacement && (
          <BlockStack gap="200">
            <StoryEntryCard
              entry={story.replacement}
              index={index}
              catalog={catalog}
              onChange={next => onChange({ ...story, replacement: next })}
              showErrors
            />
            <InlineStack>
              <Button onClick={() => onChange({ ...story, replacement: null })}>
                Cancel replacement
              </Button>
            </InlineStack>
          </BlockStack>
        )}

        {!story.removed && !story.replacement && caps.canSwitchPrinting && printing.visible && (
          <BlockStack gap="100">
            <InlineStack gap="200" blockAlign="center">
              <Text as="span" fontWeight="medium">Printing</Text>
              {premiumChanged && <Changed />}
            </InlineStack>
            {printing.editable ? (
              <div role="group" aria-label="Printing">
                <ButtonGroup variant="segmented">
                  <Button pressed={!story.premium} onClick={() => onChange({ ...story, premium: false })}>
                    Standard
                  </Button>
                  <Button pressed={story.premium} onClick={() => onChange({ ...story, premium: true })}>
                    Premium
                  </Button>
                </ButtonGroup>
              </div>
            ) : (
              <Text as="span" tone="subdued">{printing.note}</Text>
            )}
          </BlockStack>
        )}

        {!story.removed && !story.replacement && caps.canToggleAddons && story.addonsAmbiguous && (
          <Text as="p" tone="subdued">Add-ons cannot be edited for this story</Text>
        )}

        {!story.removed && editingAddons && !story.addonsAmbiguous && (
          <BlockStack gap="200">
            <InlineStack gap="400" blockAlign="center">
              <InlineStack gap="200" blockAlign="center">
                <Checkbox
                  label="Gift box"
                  checked={addons.giftBox}
                  onChange={checked => setAddons({ giftBox: checked })}
                />
                {addons.giftBox !== addonsOriginal.giftBox && <Changed />}
              </InlineStack>
              <InlineStack gap="200" blockAlign="center">
                <Checkbox
                  label="Gift note"
                  checked={addons.giftNote}
                  onChange={checked => setAddons({ giftNote: checked })}
                />
                {addons.giftNote !== addonsOriginal.giftNote && <Changed />}
              </InlineStack>
            </InlineStack>
            {addons.giftNote && (
              <BlockStack gap="100">
                <TextField
                  label="Gift message"
                  value={addons.giftMessage}
                  onChange={value => setAddons({ giftMessage: value })}
                  multiline={2}
                  autoComplete="off"
                />
                {messageChanged && <InlineStack><Changed /></InlineStack>}
              </BlockStack>
            )}
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );
}
